import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { showsEstimate } from "./accountCurrency";
import { useAccountCurrency } from "./CurrencySelector";
import { WatchCompanies } from "./ProjectWatch";
import {
  projectDescription,
  projectName,
  projectOriginalName,
  projectStructureType,
} from "./projectText";
import {
  ShowBase,
  useGetList,
  useLocaleState,
  useShowContext,
  useTranslate,
} from "ra-core";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { Identifier } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { Link } from "react-router";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type {
  FieldEvidence,
  Project,
  ProjectCompanyLink,
  ProjectDocument,
} from "../types";
import { newsDate } from "./newsAge";
import { ProjectSummary } from "./ProjectSummary";
import { Fold } from "./Fold";
import { PhaseBadge } from "./PhaseBadge";
import { ProjectActions } from "./ProjectActions";
import {
  categoryKey,
  companyRoles,
  countryName,
  formatBudget,
  formatDay,
  formatPartialDate,
  LOW_CONFIDENCE,
  roleKey,
  sourceTypeKey,
} from "./projectUtils";
import { ProjectFollowUp } from "./DealFollowUp";
import { ProjectShares } from "./ProjectShares";
import { ProjectAssistant } from "./ProjectAssistant";
import { CompanyPhone } from "./CompanyPhone";
import { CompanySite } from "./CompanySite";
import { ProjectContacts } from "./ProjectContacts";
import { ProjectHistory } from "./ProjectHistory";
import { MethodStamp } from "./MethodStamp";
import { ScoreCard } from "./ScoreCard";
import { LockNote } from "./Teaser";
import { useScreenTip, type TipSpec } from "../onboarding/useScreenTip";
import { useInvited } from "./useInvited";
import { HelpTip } from "./HelpTip";

export const ProjectShow = () => {
  const isMobile = useIsMobile();
  return (
    <ShowBase>
      {isMobile ? (
        // Sur téléphone : retour à la liste en haut, contenu dégagé de la barre de navigation.
        <>
          <MobileHeader>
            <MobileBackButton />
            <ProjectMobileTitle />
          </MobileHeader>
          <MobileContent>
            <ProjectShowContent />
            {/* Place pour la barre d'actions, posée au-dessus de la barre de navigation. */}
            <div className="h-16" />
          </MobileContent>
          <ProjectActions className="fixed inset-x-0 bottom-14 z-40" />
        </>
      ) : (
        <ProjectShowContent />
      )}
    </ShowBase>
  );
};

const ProjectMobileTitle = () => {
  const { record } = useShowContext<Project>();
  const [locale] = useLocaleState();
  return (
    <h1 className="flex-1 min-w-0 truncate text-base font-semibold">
      {projectName(record, locale)}
    </h1>
  );
};

// Sociétés visibles avant « voir les autres », dans l'ordre des rôles (maître d'ouvrage d'abord).
const SHOWN_COMPANIES = 5;

// Licence à partage à l'identique d'un document cloisonné (`payload.license`), l'ODbL à défaut.
const SHARE_ALIKE_LICENSES: Record<string, string> = {
  "ODbL 1.0": "https://opendatacommons.org/licenses/odbl/1-0/",
  "CC BY-SA 4.0": "https://creativecommons.org/licenses/by-sa/4.0/",
  "GFDL 1.3": "https://www.gnu.org/licenses/fdl-1.3.html",
};

export const ProjectShowContent = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { record, isPending } = useShowContext<Project>();
  const { currency, estimate } = useAccountCurrency();
  // Les sources sont réservées aux abonnés (la base les ferme à l'invité) : il garde le type de chaque preuve, le
  // nombre de sources et la date de la dernière nouvelle, lus par des fonctions de la base.
  const invited = useInvited();
  const tips = useMemo<TipSpec[]>(
    () => [
      { target: "stamps", key: "project.stamps" },
      {
        target: "contacts",
        key: invited ? "project.contacts_invited" : "project.contacts",
      },
    ],
    [invited],
  );
  useScreenTip("tip:project", tips, !!record);
  const { data: fullEvidence } = useGetList<FieldEvidence>(
    "field_evidence",
    {
      filter: { project_id: record?.id },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "created_at", order: "ASC" },
    },
    { enabled: !!record && !invited },
  );
  const { data: stamps } = useQuery({
    queryKey: ["project_evidence_stamps", record?.id],
    enabled: !!record && invited,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "project_evidence_stamps",
        { p_project_id: record!.id },
      );
      if (error) throw error;
      return data as FieldEvidence[];
    },
  });
  const { data: news } = useQuery({
    queryKey: ["project_news", record?.id],
    enabled: !!record && invited,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .rpc("project_news", { p_project_id: record!.id })
        .single();
      if (error) throw error;
      return data as { sources_count: number; last_news_at: string | null };
    },
  });
  const evidence = invited ? stamps : fullEvidence;
  const { data: documents } = useGetList<ProjectDocument>(
    "raw_documents",
    {
      filter: { project_id: record?.id },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "collected_at", order: "ASC" },
    },
    { enabled: !!record && !invited },
  );
  // Sources de la plus récente à la plus ancienne, datées comme dans la liste.
  const sources = [...(documents ?? [])].sort((a, b) =>
    newsDate(b).localeCompare(newsDate(a)),
  );
  const lastNews = invited
    ? (news?.last_news_at ?? null)
    : sources.length
      ? newsDate(sources[0])
      : null;
  // Chaque preuve renvoie au numéro de son document dans la liste des sources.
  const sourceNumber = (documentId: Identifier) => {
    const index = sources.findIndex(
      (document) => String(document.id) === String(documentId),
    );
    return index < 0
      ? undefined
      : { number: index + 1, document: sources[index] };
  };
  const { data: links, refetch: refetchLinks } = useGetList<ProjectCompanyLink>(
    "project_company_links",
    {
      filter: { project_id: record?.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "company_name", order: "ASC" },
    },
    { enabled: !!record },
  );
  // Au-delà des premières sociétés, la liste est repliée : un barrage en compte une vingtaine.
  const [allCompanies, setAllCompanies] = useState(false);
  const sourceInfo = useSourceInfo();
  // Fiche suivante (J / K) : liste de nouveau repliée.
  useEffect(() => setAllCompanies(false), [record?.id]);
  if (isPending || !record) return null;

  // Après un rapprochement, un champ a une preuve par document : on montre celle de la valeur affichée.
  const rawValues: Record<string, string> = {
    ...(record as unknown as Record<string, string>),
    budget_amount: `${record.budget_amount} ${record.budget_currency}`,
  };
  const evidenceFor = (field: string) => {
    const items = (evidence ?? []).filter((item) => item.field === field);
    return (
      items.find((item) => item.value === String(rawValues[field])) ?? items[0]
    );
  };
  // Les sociétés viennent des liens fiche-société : une société par rôle, quelle que soit l'écriture des sources.
  const companies = [...(links ?? [])].sort(
    (a, b) => companyRoles.indexOf(a.role) - companyRoles.indexOf(b.role),
  );
  const companyEvidence = (link: ProjectCompanyLink) =>
    (evidence ?? []).find(
      (item) =>
        item.field === `company:${link.role}` &&
        item.value === link.source_name,
    );
  const label = (field: string) =>
    translate(`resources.projects.fields.${field}`);
  const facts: [string, string | null | undefined][] = [
    ["structure_type", projectStructureType(record, locale)],
    ["country", countryName(record.country, locale)],
    ["region", record.region],
    ["city", record.city],
    [
      "budget_amount",
      showsEstimate(record, currency)
        ? `${formatBudget(record, locale)} (${estimate(record.budget_eur)})`
        : formatBudget(record, locale),
    ],
    ["funding", record.funding],
    ["start_date", formatPartialDate(record.start_date, locale)],
    ["end_date", formatPartialDate(record.end_date, locale)],
  ];

  return (
    // Mise en page selon la place disponible (panneau à côté de la liste ou page entière), pas selon l'écran.
    <div className="@container report">
      <div className="mt-2 flex flex-col @4xl:flex-row pb-2 gap-8">
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          <header className="flex flex-col gap-3">
            <div className="text-xs font-semibold tabular-nums text-muted-foreground">
              {record.aegyra_project_id}
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="text-2xl font-bold leading-tight tracking-tight text-balance">
                {projectName(record, locale)}
              </h2>
              {projectOriginalName(record, locale) && (
                <p className="text-sm text-muted-foreground">
                  {translate("resources.projects.original_name", {
                    name: projectOriginalName(record, locale),
                  })}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Badge variant="outline">
                {translate(categoryKey(record.category))}
              </Badge>
              <PhaseBadge phase={record.phase} />
              {evidenceFor("phase") && (
                <Stamp evidence={evidenceFor("phase")!} />
              )}
            </div>
            <ProjectSummary project={record} lastNews={lastNews} />
            {projectDescription(record, locale) && (
              <p className="text-[15px] leading-relaxed">
                {projectDescription(record, locale)}
              </p>
            )}
          </header>

          {/* Bulle « Sourcé ou déduit » : sur les faits, seulement quand l'un d'eux porte un tampon. */}
          <Card
            data-tour={
              facts.some(([field, value]) => value && evidenceFor(field))
                ? "stamps"
                : undefined
            }
          >
            <CardContent>
              <SectionTitle>
                <span className="inline-flex items-center gap-1">
                  {translate("resources.projects.sections.facts")}
                  <HelpTip topic="evidence" />
                </span>
              </SectionTitle>
              <dl className="divide-y divide-border">
                {facts
                  .filter(([, value]) => value)
                  .map(([field, value]) => (
                    <Entry
                      key={field}
                      label={label(field)}
                      value={value}
                      evidence={evidenceFor(field)}
                      sourceNumber={sourceNumber}
                    />
                  ))}
              </dl>
            </CardContent>
          </Card>

          <Card data-anchor="sources">
            <CardContent>
              {invited ? (
                <>
                  <SectionTitle>
                    {translate("resources.projects.sections.sources_count", {
                      smart_count: news?.sources_count ?? 0,
                    })}
                  </SectionTitle>
                  <LockNote>
                    {translate("resources.projects.teaser.sources")}
                  </LockNote>
                </>
              ) : (
                // Le détail des documents se lit à la demande ; chaque donnée renvoie déjà à son numéro de source.
                <Fold
                  summary={translate(
                    "resources.projects.sections.sources_count",
                    {
                      smart_count: sources.length,
                    },
                  )}
                  summaryClassName="text-sm font-bold text-foreground"
                  openOnJump
                >
                  <ul className="mt-4 flex flex-col gap-4">
                    {sources.map((document, index) => (
                      <li
                        key={document.id}
                        className="grid grid-cols-[1.5rem_1fr] gap-x-2 text-sm"
                      >
                        <SourceNumber n={index + 1} />
                        <div className="min-w-0">
                          <a
                            href={document.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium underline inline-flex gap-1 items-start"
                          >
                            <span>{document.title}</span>
                            <ExternalLink className="h-3 w-3 mt-1 shrink-0" />
                          </a>
                          {/* Le seul endroit où le nom de la source est donné, avec son type. */}
                          <div className="text-xs text-muted-foreground mt-1">
                            {[
                              sourceInfo(document.source_id).type,
                              sourceInfo(document.source_id).name,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {document.source_id === "worldbank" ||
                            !document.published_at
                              ? translate("resources.projects.collected_on", {
                                  date: formatDay(
                                    document.collected_at,
                                    locale,
                                  ),
                                })
                              : translate(
                                  "resources.projects.published_and_collected",
                                  {
                                    published: formatDay(
                                      document.published_at,
                                      locale,
                                    ),
                                    collected: formatDay(
                                      document.collected_at,
                                      locale,
                                    ),
                                  },
                                )}
                          </div>
                          {document.payload?.attribution ? (
                            <div className="text-xs text-muted-foreground italic mt-1">
                              {translate("resources.projects.odbl_notice", {
                                publisher: document.payload.attribution,
                              })}{" "}
                              <a
                                href={
                                  SHARE_ALIKE_LICENSES[
                                    document.payload.license ?? "ODbL 1.0"
                                  ] ?? SHARE_ALIKE_LICENSES["ODbL 1.0"]
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline"
                              >
                                {document.payload.license ?? "ODbL 1.0"}
                              </a>
                            </div>
                          ) : null}
                          {document.source_id === "postor" ? (
                            <div className="text-xs text-muted-foreground italic mt-1">
                              {translate("resources.projects.postor_notice", {
                                date: formatDay(document.collected_at, locale),
                                citation: document.payload?.citation ?? "",
                              })}{" "}
                              <a
                                href="https://creativecommons.org/licenses/by/4.0/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline"
                              >
                                CC BY 4.0
                              </a>
                            </div>
                          ) : null}
                          {document.source_id === "toronto" ? (
                            <div className="text-xs text-muted-foreground italic mt-1">
                              {translate("resources.projects.toronto_notice")}
                            </div>
                          ) : null}
                          {document.source_id === "boe" ? (
                            <div className="text-xs text-muted-foreground italic mt-1">
                              {translate("resources.projects.boe_notice")}{" "}
                              <a
                                href="https://www.boe.es"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline"
                              >
                                Agencia Estatal Boletín Oficial del Estado
                              </a>
                            </div>
                          ) : null}
                          {document.source_id === "chicago" ? (
                            <div className="text-xs text-muted-foreground italic mt-1">
                              {translate("resources.projects.chicago_notice")}
                            </div>
                          ) : null}
                          {document.source_id === "simap" ? (
                            <div className="text-xs text-muted-foreground italic mt-1">
                              {translate("resources.projects.simap_notice")}
                            </div>
                          ) : null}
                          {document.source_id === "jpeia" ? (
                            <div className="text-xs text-muted-foreground italic mt-1">
                              {translate("resources.projects.jpeia_notice")}{" "}
                              <a
                                href="https://www.digital.go.jp/resources/open_data/public_data_license_v1.0"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline"
                              >
                                PDL 1.0
                              </a>
                            </div>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                </Fold>
              )}
            </CardContent>
          </Card>

          {/* Sur une seule colonne (panneau, téléphone), les faits sourcés d'abord, puis le suivi et le score. */}
          <div className="@4xl:hidden flex flex-col gap-4">
            <ProjectFollowUp project={record} />
            <ScoreCard project={record} />
          </div>

          <Card>
            <CardContent>
              <SectionTitle>
                {translate("resources.projects.sections.companies")}
              </SectionTitle>
              {companies.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {translate("resources.projects.no_companies")}
                </p>
              ) : (
                <dl>
                  {companies
                    .slice(0, allCompanies ? undefined : SHOWN_COMPANIES)
                    .map((link, index) => (
                      <Entry
                        key={link.id}
                        label={translate(roleKey(link.role), { _: link.role })}
                        // Plusieurs sociétés au même rôle (trois maîtres d'ouvrage) : le rôle n'est écrit qu'une fois.
                        continued={companies[index - 1]?.role === link.role}
                        value={
                          <>
                            <Link
                              to={`/companies/${link.company_id}/show`}
                              className="underline"
                            >
                              {link.company_name}
                            </Link>
                            <CompanySite
                              companyId={link.company_id}
                              domain={link.company_domain}
                              website={link.company_website}
                              method={link.domain_method}
                              sourceUrl={link.domain_source_url}
                              foundAt={link.domain_found_at}
                              onReported={() => refetchLinks()}
                            />
                            <CompanyPhone
                              number={link.company_phone}
                              method={link.phone_method}
                              sourceUrl={link.phone_source_url}
                              foundAt={link.phone_found_at}
                            />
                          </>
                        }
                        evidence={companyEvidence(link)}
                        sourceNumber={sourceNumber}
                      />
                    ))}
                </dl>
              )}
              {!allCompanies && companies.length > SHOWN_COMPANIES && (
                <button
                  type="button"
                  className="mt-3 w-fit rounded-[3px] text-sm font-medium underline underline-offset-2"
                  onClick={() => setAllCompanies(true)}
                >
                  {translate("resources.projects.companies_show_more", {
                    smart_count: companies.length - SHOWN_COMPANIES,
                  })}
                </button>
              )}
              <WatchCompanies
                project={record}
                roles={companies.map((link) => link.role)}
              />
            </CardContent>
          </Card>

          <ProjectContacts project={record} />

          <ProjectAssistant project={record} />

          <ProjectHistory project={record} documents={documents} />
        </div>

        <div className="@4xl:w-80 @4xl:min-w-80 flex flex-col gap-4">
          {/* Le suivi d'abord : c'est l'action attendue sur la fiche, le score est long. */}
          <div className="hidden @4xl:flex flex-col gap-4">
            <ProjectFollowUp project={record} />
            <ScoreCard project={record} />
          </div>
          <ProjectShares project={record} />
        </div>
      </div>
    </div>
  );
};

/** Nom lisible d'une source (« TED Europe ») ; les recherches Google News partagent le même nom. Une liste
 * courte (une centaine de sources), lue une fois et gardée en cache par ra-core. */
// Nom et type de chaque source : le type sert de rappel sous les données, le nom n'est donné que dans la section
// « Sources » (décision de Maxime du 05/11/2026).
const useSourceInfo = () => {
  const translate = useTranslate();
  const { data } = useGetList<{
    id: string;
    name: string;
    source_type: string | null;
  }>("sources", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "id", order: "ASC" },
  });
  return (id: string) => {
    const source = data?.find((item) => item.id === id);
    return {
      name: source?.name.split(" — ")[0] ?? "",
      type: source?.source_type
        ? translate(sourceTypeKey(source.source_type))
        : "",
    };
  };
};

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <h3 className="text-sm font-bold mb-4">{children}</h3>
);

/** Une ligne du relevé : libellé, valeur et tampon, puis la citation et le document d'où vient la valeur. */
const Entry = ({
  label,
  value,
  evidence,
  sourceNumber,
  continued,
}: {
  label: string;
  value: ReactNode;
  evidence?: FieldEvidence;
  // Suite d'un groupe au même libellé : libellé gardé pour les lecteurs d'écran, sans trait ni répétition à l'œil.
  continued?: boolean;
  sourceNumber: (
    documentId: Identifier,
  ) => { number: number; document: ProjectDocument } | undefined;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const source = evidence ? sourceNumber(evidence.document_id) : undefined;
  const sourceInfo = useSourceInfo();
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-x-4 gap-y-1 py-3 first:pt-0 last:pb-0 @md:grid-cols-[9rem_1fr]",
        // Groupe de sociétés : trait au-dessus de chaque rôle (pas de « divide-y », qui trace sous la ligne d'avant).
        continued === false && "border-t border-border first:border-t-0",
        continued && "pt-0",
      )}
    >
      <dt
        className={cn(
          "pt-0.5 text-xs font-semibold text-muted-foreground",
          continued && "sr-only @md:not-sr-only @md:invisible",
        )}
      >
        {label}
      </dt>
      <dd className="min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 text-sm font-medium tabular-nums">
            {value}
          </div>
          {evidence && <Stamp evidence={evidence} />}
        </div>
        {evidence?.method === "inferred" &&
          evidence.confidence < LOW_CONFIDENCE && (
            <p className="mt-1 text-xs font-medium text-inferred-ink">
              {translate("resources.projects.to_check")}
            </p>
          )}
        {evidence?.method === "extracted" && evidence.quote && (
          <p className="mt-1 line-clamp-3 text-xs italic text-muted-foreground">
            « {evidence.quote} »
          </p>
        )}
        {source && (
          <a
            href={source.document.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 block text-xs text-muted-foreground hover:text-foreground hover:underline"
          >
            {/* Texte courant plutôt que flex : sur téléphone, le nom de la source passe à la ligne sans couper « Source 1 ». */}
            <span className="whitespace-nowrap tabular-nums">
              {translate("resources.projects.source_ref", {
                n: source.number,
              })}
            </span>
            {sourceInfo(source.document.source_id).type && (
              <> · {sourceInfo(source.document.source_id).type}</>
            )}
            {" · "}
            <span className="whitespace-nowrap tabular-nums">
              {formatDay(
                source.document.published_at ?? source.document.collected_at,
                locale,
              )}
            </span>
          </a>
        )}
      </dd>
    </div>
  );
};

/** Tampon de méthode : la valeur est citée dans la source, déduite par l'IA (avec sa confiance) ou saisie. */
const Stamp = ({ evidence }: { evidence: FieldEvidence }) => {
  const translate = useTranslate();
  return (
    <MethodStamp
      method={evidence.method}
      title={translate(`resources.projects.methods.${evidence.method}`)}
      label={
        translate(`resources.projects.stamps.${evidence.method}`) +
        (evidence.method === "inferred"
          ? ` · ${translate("resources.projects.confidence", {
              value: Math.round(evidence.confidence * 100),
            })}`
          : "")
      }
    />
  );
};

const SourceNumber = ({ n }: { n: number }) => (
  <span className="mt-0.5 flex size-5 items-center justify-center rounded-[3px] border border-border text-[11px] font-semibold tabular-nums text-muted-foreground">
    {n}
  </span>
);
